"use client"

import { useEffect } from "react"
import { clientAPI } from "@/lib/client-fetch"
import { snakeToCamel } from "@/lib/utils"
import API_ENDPOINTS from "@/constants/api-endpoints"
import { useCouponStore } from "@/lib/coupons/coupon-store"
import type { CouponDetail } from "@/lib/coupons/types"

type CouponDetailsResponse = {
  coupons: CouponDetail[]
  autoAppliedCoupons: CouponDetail[]
  invalidCodes: string[]
}

/**
 * Standalone function to fetch coupon details for the given user-entered codes
 * plus any auto-applied coupons from the server. Updates the coupon store in place.
 */
export async function fetchAndSyncCouponDetails(appliedCodes: string[]) {
  const { setCouponDetails, setAutoAppliedCodes, removeCode } = useCouponStore.getState()

  const result = await clientAPI.post(API_ENDPOINTS.COUPON_UPDATES, { coupon_codes: appliedCodes })
  if (!result.ok) return

  const data = snakeToCamel(result.data) as CouponDetailsResponse

  const details: Record<string, CouponDetail> = {}
  for (const coupon of data.coupons ?? []) {
    details[coupon.code] = coupon
  }
  for (const coupon of data.autoAppliedCoupons ?? []) {
    details[coupon.code] = coupon
  }

  setCouponDetails(details)
  setAutoAppliedCodes((data.autoAppliedCoupons ?? []).map((c) => c.code))

  for (const code of data.invalidCodes ?? []) {
    removeCode(code)
  }
}

/**
 * Hook that fetches coupon details and auto-applied coupons on mount.
 * Mount this on pages where coupon state needs to be hydrated (checkout, catalog).
 */
export function useFetchCoupons() {
  const appliedCodes = useCouponStore((s) => s.appliedCodes)

  useEffect(() => {
    fetchAndSyncCouponDetails(appliedCodes)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only on mount — intent is to hydrate once per page load
}
